import {
  clearNuxtData,
  useAsyncData,
  useNuxtApp,
  useRouteLocationWithLocale,
} from '#imports'
import { type Ref, computed, onScopeDispose, ref } from 'vue'

import type {
  AlgoliaLink,
  AlgoliaProduct,
} from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia.ts'
import type { SearchBarConfiguration } from '@backmarket/http-api/src/api-specs-search-reco/search/searchConfiguration.ts'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import algoliasearch from 'algoliasearch'

import { useBrowsingHistory } from '~/scopes/browsing-history/composables/useBrowsingHistory'
import type { BrowsingHistoryEntry } from '~/scopes/browsing-history/types'
import { ROUTES as PRODUCT_LIST_ROUTES } from '~/scopes/product-list/route-names'
import {
  algoliaHitToLink,
  algoliaHitToProduct,
} from '~/scopes/search/algolia/algoliaFunctions'

import type { RecentlyViewedPage } from './useRecentlyViewedPages.types'

export function useRecentlyViewedPages(
  configuration: Ref<SearchBarConfiguration | undefined>,
) {
  const experiments = useExperiments()
  const logger = useLogger()
  const app = useNuxtApp()
  const resolveLocalizedRoute = useRouteLocationWithLocale()

  const isBrowsingHistoryEnabled =
    experiments['experiment.browsingHistoryPanel'] === 'enabled'
  const algoliaSearchOption = useAlgoliaSearchOptions('search')
  const algoliaClient = computed(() => {
    if (!configuration.value) return null

    return algoliasearch('dummy_app_id', 'dummy_key', algoliaSearchOption)
  })

  // Browsing history state, fetched on demand when the recently viewed pages run.
  const historyEntries = ref<BrowsingHistoryEntry[]>([])
  const { fetch: fetchHistoryEntries } = useBrowsingHistory()

  async function fetchAllHistoryEntries() {
    const [pdpEntries, plpAndSearchEntries] = await Promise.all([
      fetchHistoryEntries({
        kind: ['pdp'],
        limit: 6,
      }),
      fetchHistoryEntries({
        kind: ['plp', 'search'],
        limit: 6,
      }),
    ])

    historyEntries.value = [...pdpEntries, ...plpAndSearchEntries]
  }

  const unhookBrowsingHistoryUpdate = app.hook(
    'browsing-history:update',
    () => {
      clearNuxtData('recently-viewed-pages')
    },
  )
  onScopeDispose(unhookBrowsingHistoryUpdate)

  return useAsyncData(
    'recently-viewed-pages',
    async () => {
      if (
        !isBrowsingHistoryEnabled ||
        !algoliaClient.value ||
        !configuration.value
      )
        return null

      await fetchAllHistoryEntries()

      const pdpIndexName =
        configuration.value.backboxWinners.indexes.active.name
      const plpIndexName = configuration.value.landingPages.indexes.active.name

      const pdpFilters = historyEntries.value
        .flatMap((entry) =>
          entry.kind === 'pdp'
            ? `objectID:"${entry.productId}_${entry.grade}_${entry.offerType}"`
            : [],
        )
        .join(' OR ')

      const plpFilters = historyEntries.value
        .flatMap((entry) =>
          entry.kind === 'plp' ? `objectID:"${entry.landingId}"` : [],
        )
        .join(' OR ')

      const requests = []

      if (pdpFilters.length > 0) {
        requests.push({
          indexName: pdpIndexName,
          params: { filters: pdpFilters },
        })
      }

      if (plpFilters.length > 0) {
        requests.push({
          indexName: plpIndexName,
          params: { filters: plpFilters },
        })
      }

      if (requests.length === 0) return null

      try {
        const { results } = await algoliaClient.value.search<
          AlgoliaProduct | AlgoliaLink
        >(requests)

        return results.flatMap((result) =>
          'hits' in result ? result.hits : [],
        )
      } catch (error) {
        logger.error('[S&R] Recently viewed pages search failed', {
          type: 'API_REQUEST_ERROR',
          error,
          owners: ['bot-squad-search-and-recommendation-front'],
        })

        throw error
      }
    },
    {
      default: () => [] as RecentlyViewedPage[],
      transform: (hits) => {
        const hitsByObjectId = new Map(
          (hits ?? [])
            .filter((hit) => hit !== null)
            .map((hit) => [hit.objectID, hit]),
        )

        return historyEntries.value.flatMap<RecentlyViewedPage>((entry) => {
          switch (entry.kind) {
            case 'search': {
              return [
                {
                  id: entry.id,
                  highlightedTitle: entry.term,
                  title: entry.term,
                  url: resolveLocalizedRoute({
                    name: PRODUCT_LIST_ROUTES.SEARCH_RESULTS,
                    query: {
                      q: entry.term,
                    },
                  }),
                  entryType: 'search-query',
                  timestamp: entry.lastVisitedAt,
                },
              ]
            }
            case 'plp': {
              const hit = hitsByObjectId.get(entry.landingId) as
                | AlgoliaLink
                | undefined

              if (!hit) return []

              return [
                {
                  ...algoliaHitToLink(hit),
                  entryType: 'plp',
                  timestamp: entry.lastVisitedAt,
                },
              ]
            }
            case 'pdp': {
              const objectID = `${entry.productId}_${entry.grade}_${entry.offerType}`
              const hit = hitsByObjectId.get(objectID) as
                | AlgoliaProduct
                | undefined

              if (!hit) return []

              return [
                {
                  ...algoliaHitToProduct(hit),
                  entryType: 'pdp',
                  timestamp: entry.lastVisitedAt,
                },
              ]
            }
            default:
              return []
          }
        })
      },
      server: false,
      immediate: false,
      getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] || undefined,
      dedupe: 'defer',
    },
  )
}
